import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Users, UserCog, CalendarClock, AlertCircle, ArrowUpRight, GitMerge, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

function statusPill(status) {
  if (status === "scheduled") return "bg-[#E8F0EA] text-[#274f38]";
  if (status === "cancelled") return "bg-[#F0EFEA] text-[#A1ACA6]";
  return "bg-[#FDF4E7] text-[#B07C60]";
}

function Stat({ icon: Icon, label, value, iconBg, borderColor, testId }) {
  return (
    <div
      data-testid={testId}
      className="bg-surface border border-soft rounded-lg overflow-hidden card-shadow transition-all hover:shadow-[0_8px_24px_rgba(24,35,30,0.08)] hover:-translate-y-0.5"
      style={{ borderTop: `3px solid ${borderColor}` }}
    >
      <div className="p-6 flex items-start justify-between">
        <div>
          <div className="text-xs tracking-[0.18em] uppercase text-muted-ohana font-semibold">{label}</div>
          <div className="mt-3 text-4xl font-medium tracking-tight" style={{ fontFamily: "Outfit" }}>{value}</div>
        </div>
        <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: iconBg }}>
          <Icon size={20} strokeWidth={1.6} className="text-[#274f38]" />
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState({ therapists: 0, clients: 0, upcoming_sessions: 0, unassigned_clients: 0 });
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [s, sess] = await Promise.all([
          api.get("/dashboard/stats"),
          api.get("/sessions"),
        ]);
        setStats(s.data);
        setSessions(sess.data.slice(0, 8));
      } catch {}
    })();
  }, []);

  return (
    <div>
      <PageHeader
        title="Dashboard"
        subtitle="Operational overview of your clinical scheduling."
      />
      <div className="px-10 py-8 space-y-8">
        <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Stat
            icon={Users}
            label="Active clients"
            value={stats.clients}
            iconBg="#E8F0EA"
            borderColor="#C8DDD0"
            testId="stat-clients"
          />
          <Stat
            icon={UserCog}
            label="Therapists"
            value={stats.therapists}
            iconBg="#F0EFEA"
            borderColor="#DCD9D2"
            testId="stat-therapists"
          />
          <Stat
            icon={CalendarClock}
            label="Upcoming sessions"
            value={stats.upcoming_sessions}
            iconBg="#EAF0F5"
            borderColor="#C8D8E8"
            testId="stat-upcoming"
          />
          <Stat
            icon={AlertCircle}
            label="Unassigned clients"
            value={stats.unassigned_clients}
            iconBg="#FDF4E7"
            borderColor="#F0D9A8"
            testId="stat-unassigned"
          />
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-surface border border-soft rounded-lg card-shadow">
            <div className="flex items-center justify-between px-6 py-4 border-b border-soft">
              <h3 className="text-lg font-medium" style={{ fontFamily: "Outfit" }}>Upcoming sessions</h3>
              <Link to="/admin/schedule" className="text-sm text-[#274f38] hover:text-[#1E3D2B] inline-flex items-center gap-1" data-testid="view-schedule-link">
                View schedule <ArrowUpRight size={14} />
              </Link>
            </div>
            <div className="divide-y divide-[#E3DFD5]">
              {sessions.length === 0 && (
                <div className="p-8 text-center text-muted-ohana text-sm">No sessions scheduled yet.</div>
              )}
              {sessions.map((s) => (
                <div key={s.id} className="px-6 py-3.5 flex items-center justify-between" data-testid={`session-row-${s.id}`}>
                  <div>
                    <div className="font-medium text-sm text-[#18231E]">{formatDate(s.date)}</div>
                    {s.therapist_name && (
                      <div className="text-xs text-[#586960] mt-0.5">{s.therapist_name}</div>
                    )}
                    <div className="font-[JetBrains_Mono,monospace] text-xs text-muted-ohana mt-0.5">{s.start_time} – {s.end_time}</div>
                  </div>
                  <div className="flex gap-2 items-center flex-wrap justify-end">
                    {s.rest_break_required && <span className="text-xs px-2 py-0.5 rounded-full bg-[#FDF4E7] text-[#B07C60]">Rest break</span>}
                    {s.lunch_break_required && <span className="text-xs px-2 py-0.5 rounded-full bg-[#FDF4E7] text-[#B07C60]">Lunch break</span>}
                    <span className={`text-xs px-2.5 py-0.5 rounded-full capitalize font-medium ${statusPill(s.status)}`}>{s.status}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-surface border border-soft rounded-lg p-6 card-shadow">
            <h3 className="text-lg font-medium mb-1" style={{ fontFamily: "Outfit" }}>Quick actions</h3>
            <p className="text-sm text-muted-ohana mb-5">Jump to common workflows.</p>
            <div className="space-y-2">
              <Link
                to="/admin/clients"
                className="flex items-center gap-3 px-4 py-3 rounded-md border border-soft hover:bg-[#F7FAF8] hover:border-[#274f38] transition-all duration-150"
                data-testid="action-add-client"
              >
                <Users size={18} strokeWidth={1.6} className="text-[#274f38] shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[#18231E]">Add a client</div>
                  <div className="text-xs text-muted-ohana">Create intake with availability, insurance, and preferences.</div>
                </div>
                <ChevronRight size={16} className="text-muted-ohana shrink-0" />
              </Link>
              <Link
                to="/admin/therapists"
                className="flex items-center gap-3 px-4 py-3 rounded-md border border-soft hover:bg-[#F7FAF8] hover:border-[#274f38] transition-all duration-150"
                data-testid="action-add-therapist"
              >
                <UserCog size={18} strokeWidth={1.6} className="text-[#274f38] shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[#18231E]">Add a therapist</div>
                  <div className="text-xs text-muted-ohana">Capture skills, availability, and home address.</div>
                </div>
                <ChevronRight size={16} className="text-muted-ohana shrink-0" />
              </Link>
              <Link
                to="/admin/matching"
                className="flex items-center gap-3 px-4 py-3 rounded-md border border-soft hover:bg-[#F7FAF8] hover:border-[#274f38] transition-all duration-150"
                data-testid="action-matching"
              >
                <GitMerge size={18} strokeWidth={1.6} className="text-[#274f38] shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[#18231E]">Run matching</div>
                  <div className="text-xs text-muted-ohana">Get prioritized therapist candidates for a client.</div>
                </div>
                <ChevronRight size={16} className="text-muted-ohana shrink-0" />
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
